/**
 * 動画エディタ 編集プロジェクト CRUD API クライアント。
 *
 * backend/routes/video_edit_projects.py (platform admin 限定) に対応する。
 * project (jsonb) は署名付き URL を含めずに保存する — 送信前に必ず
 * stripResolvedUrls() を通す (バックエンドの _strip_resolved_urls と二重防御)。
 */
import { isAxiosError } from 'axios';
import { apiClient } from '../../services/apiClient';
import type {
  AudioCue,
  BgmAnalysis,
  Ducking,
  EditProject,
  LegacyBgm,
  Freeze,
  Look,
  Loudness,
  NarrationCue,
  Overlay,
  SeCandidate,
  SpeedRamp,
  TimelineItem,
  TrackMutes,
} from './schema/project';
import type { CaptionWordHighlight, CaptionWordTimings } from './schema/captionWordHighlight';
import type { LookPreset } from './schema/lookCatalog';
import { SCENARIO_MODEL_CATALOG } from '../../components/generation/scenario/scenarioModelCatalog';
import { SCENARIO_MODEL_LEGACY_MAP, type ScenarioModelType } from '../../components/generation/state/modelCatalog';

/** 一覧レスポンス (project 本体は含まない)。 */
export interface VideoEditProjectSummary {
  id: string;
  /** 編集元シナリオ。素材起点 (origin='material') のプロジェクトは null。 */
  scenario_id: string | null;
  name: string;
  revision: number;
  created_at: string;
  updated_at: string;
  user_id: string;
  /**
   * 出自 (docs/features/video-editor-presentation-import-plan.md §3.3)。'scenario' = シナリオから組み立てた
   * 従来のプロジェクト、'material' = アップロード済み動画素材から直接作ったプロジェクト。
   * 旧 BE の応答では欠落するので optional (欠落 = 'scenario' として扱う)。
   */
  origin?: VideoEditProjectOrigin;
  /** origin='material' のときの元素材 ID。 */
  origin_material_id?: string | null;
  /** 元素材が個人フォルダ (temporary_materials) か。 */
  origin_material_is_temporary?: boolean | null;
  /** 取り込みジョブ (video_edit_analyze) の現ジョブ ID。null = 未受付。再読み込み時はこれに再接続する。 */
  import_job_id?: string | null;
  /** 取り込みの世代 (importVersion の正本)。受付ごとに進む。 */
  import_version?: number;
  /** 編集に紐づけた手動登録の案件 (products.id・素材起点のみ。video-import-motion-scenario-plan.md §3.1)。null = 未設定 */
  product_id?: string | null;
  /** 編集に紐づけた Amazon 案件 (amazon_products.id)。product_id とどちらか片方だけ (video-import-scenario-key-points-plan.md §12) */
  amazon_product_id?: string | null;
  /** 紐づけた案件の解決済みの情報 (詳細と PATCH の応答)。null = 未設定 */
  linked_product?: LinkedProduct | null;
}

/** 編集に紐づけた案件 (手動登録 / Amazon)。 */
export interface LinkedProduct {
  product_type: 'manual' | 'amazon';
  product_id: string;
  title: string | null;
  /** Amazon 案件の ASIN (手動登録は null) */
  asin: string | null;
}

/** 編集プロジェクトの出自。 */
export type VideoEditProjectOrigin = 'scenario' | 'material';

/** 詳細レスポンス (project jsonb + revision を含む)。 */
export interface VideoEditProjectDetail extends VideoEditProjectSummary {
  organization_id: string;
  /**
   * 素材起点の作成で、同じ素材の取り込み済みの編集から解析結果を流用して取り込み済みで作ったとき (作成応答だけに載る)。
   * 取り込みジョブは起動されず、ポイントも消費していない
   */
  import_reused_from?: { project_id: string; name: string | null } | null;
  // サーバーはバリデーション済み jsonb をそのまま返す。フロントは必ず
  // validateProject() を通してから EditProject として扱う。
  project: unknown;
  /**
   * 未適用の AI 編集提案 (video_edit_projects.pending_proposal)。提案ジョブの executor が完了時に
   * 保存し、提案中にエディタを離れても開き直したときに適用できるようにする。適用/破棄で
   * clearPendingProposal() により null に戻す。無ければ null / 欠落。
   */
  pending_proposal?: PendingProposalRecord | null;
  /** 新規作成時のみ。歌声と対応づけできたカラオケ字幕数。 */
  lyrics_caption_sync?: { status: string; reason?: string; aligned: number; unaligned: number };
}

/**
 * PUT が 409 (楽観ロック競合) を返したときに throw するエラー。
 * 呼び出し側 (SaveBar) はこれを捕捉して再読み込みを促す。
 */
export class ConflictError extends Error {
  /** 'format_outdated' = 保存済みの編集データより古い形式の本文 (旧タブ。lineage 計画 §8.2)。省略 = 楽観ロックの競合。 */
  reason: 'format_outdated' | undefined;
  constructor(message = '他の画面で更新されています。再読み込みしてください', reason?: 'format_outdated') {
    super(message);
    this.name = 'ConflictError';
    this.reason = reason;
  }
}

export async function listEditProjects(scenarioId: string): Promise<VideoEditProjectSummary[]> {
  const { data } = await apiClient.get<{ projects: VideoEditProjectSummary[] }>(
    '/video-edit-projects',
    { params: { scenario_id: scenarioId } },
  );
  return data.projects ?? [];
}

/**
 * 素材起点 (origin='material') のプロジェクト一覧 (プレゼン動画取り込み計画 §4.1)。
 * `?scenario_id=` とは排他で、シナリオに紐づかないプロジェクトだけを返す。
 */
export async function listMaterialEditProjects(): Promise<VideoEditProjectSummary[]> {
  const { data } = await apiClient.get<{ projects: VideoEditProjectSummary[] }>(
    '/video-edit-projects',
    { params: { origin: 'material' } },
  );
  return data.projects ?? [];
}

/** シナリオから組み立てる従来の作成パラメータ。 */
export interface CreateEditProjectFromScenarioParams {
  scenarioId: string;
  name: string;
  project: EditProject;
  initializeLyricsCaptions?: boolean;
}

/**
 * アップロード済み動画素材から直接作る作成パラメータ (プレゼン動画取り込み計画 §4.1)。
 * requestId は入口のボタン押下ごとに発行する UUID で、作成〜取り込み受付 (`importEditProject`) まで
 * 同じ値を使い回す。同じ requestId の再送には BE が既存のプロジェクトを返す (二重作成の防止)。
 */
export interface CreateEditProjectFromMaterialParams {
  origin: 'material';
  materialId: string;
  isTemporary: boolean;
  requestId: string;
  name: string;
  project: EditProject;
}

export async function createEditProject(
  params: CreateEditProjectFromScenarioParams | CreateEditProjectFromMaterialParams,
): Promise<VideoEditProjectDetail> {
  const body = 'origin' in params
    ? {
        origin: 'material' as const,
        material_id: params.materialId,
        is_temporary: params.isTemporary,
        request_id: params.requestId,
        name: params.name,
        project: stripResolvedUrls(params.project),
      }
    : {
        scenario_id: params.scenarioId,
        name: params.name,
        project: stripResolvedUrls(params.project),
        ...(params.initializeLyricsCaptions ? { initialize_lyrics_captions: true } : {}),
      };
  const { data } = await apiClient.post<VideoEditProjectDetail>('/video-edit-projects', body);
  return data;
}

/** POST /video-edit-projects/:id/import の応答 (202)。 */
export interface ImportEditProjectResponse {
  /** 受け付けた (または実行中の) 取り込みジョブ。status='done' のときは null。 */
  job_id: string | null;
  /**
   * accepted = 新規受付 / in_progress = 実行中のジョブを返した (同じ request_id の再送・resume 含む) /
   * done = 完了済みで何もしない / failed・none = resume 専用で、終端失敗・ジョブ無し (受付していない)。
   */
  status: 'accepted' | 'in_progress' | 'done' | 'failed' | 'none';
  import_version: number;
}

/**
 * 素材起点プロジェクトの取り込みジョブ (文字起こし・意味解析) を起動または再接続する (計画 §4.1)。
 * 冪等: 同じ requestId の再送は同じジョブを返す。mode='start' は初回受付、'force' は明示的な再解析。
 * origin='scenario' のプロジェクトには使えない (BE が 400)。状態確認は GET /video-edit-projects/:id の
 * import_job_id / project.importAnalyzed で行い、この API は利用者の操作 (入口・再解析) でだけ呼ぶ。
 */
export async function importEditProject(
  projectId: string,
  params: { requestId: string; mode: 'start' | 'force' | 'resume'; quotedCredits?: number | null },
): Promise<ImportEditProjectResponse> {
  // quoted_credits (任意): 画面に出した料金。BE の料金と違えば 409 (料金改定後の古い表示で受け付けない)。
  // resume は課金しないので送らない
  const body: Record<string, unknown> = { request_id: params.requestId, mode: params.mode };
  if (params.mode !== 'resume' && typeof params.quotedCredits === 'number') {
    body.quoted_credits = params.quotedCredits;
  }
  const { data } = await apiClient.post<ImportEditProjectResponse>(`/video-edit-projects/${projectId}/import`, body);
  return data;
}

export async function getEditProject(id: string): Promise<VideoEditProjectDetail> {
  const { data } = await apiClient.get<VideoEditProjectDetail>(`/video-edit-projects/${id}`);
  return data;
}

export async function updateEditProject(
  id: string,
  params: { project: EditProject; name?: string; expectedRevision: number },
): Promise<VideoEditProjectDetail> {
  try {
    const { data } = await apiClient.put<VideoEditProjectDetail>(`/video-edit-projects/${id}`, {
      project: stripResolvedUrls(params.project),
      ...(params.name !== undefined ? { name: params.name } : {}),
      expected_revision: params.expectedRevision,
    });
    return data;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 409) {
      const body = error.response.data as { error?: unknown; reason?: unknown } | undefined;
      if (body?.reason === 'format_outdated') {
        throw new ConflictError(
          typeof body.error === 'string' ? body.error : 'エディタが古いため保存できません。再読み込みしてください',
          'format_outdated',
        );
      }
      throw new ConflictError();
    }
    throw error;
  }
}

export async function deleteEditProject(id: string): Promise<void> {
  await apiClient.delete(`/video-edit-projects/${id}`);
}

// ── AI 編集提案 (Phase E) ──────────────────────────────────────────
// backend/services/video_edit/propose_service.py + backend/routes/async_jobs/video_edit.py に対応。
// 選択肢はシナリオ編集ページのモデルカタログ (SCENARIO_MODEL_CATALOG) のうち GPT / Claude をそのまま使う
// (2026-09-24: 旧 Fable 5 / Opus 5 / GPT-5.6 系の独自残置をやめ、シナリオ側と同期させる)。
// backend の propose_service.ALLOWED_MODELS と同期する (test_scenario_random_pool_sync が突き合わせる)。

export type ProposeModelId = Exclude<ScenarioModelType, `gemini-${string}`>;

export interface ProposeModelSpec {
  id: ProposeModelId;
  label: string;
  family: string;
  icon: string;
  tier: string;
  name: string;
}

export const PROPOSE_ALLOWED_MODELS: readonly ProposeModelSpec[] = SCENARIO_MODEL_CATALOG
  .filter((spec) => spec.provider !== 'Google')
  .map((spec) => ({
    id: spec.value as ProposeModelId,
    label: `${spec.family} ${spec.modelName}(${spec.tier})`,
    family: spec.family,
    icon: spec.icon,
    tier: spec.tier,
    name: spec.modelName,
  }));

export const PROPOSE_DEFAULT_MODEL: ProposeModelId = 'gpt-6-astra';

// 廃止したモデル id の読み替え。シナリオ側の SCENARIO_MODEL_LEGACY_MAP のうち飛び先がこの画面の選択肢に
// あるものをそのまま使う (backend は propose_service.LEGACY_MODEL_ALIASES が同じ導出をする)。
// localStorage の保存値・演出プランの context.model に旧 id が残っていても後継で続けられるようにする。
export const PROPOSE_LEGACY_MODEL_MAP: Readonly<Record<string, ProposeModelId>> = Object.fromEntries(
  Object.entries(SCENARIO_MODEL_LEGACY_MAP)
    .filter((entry): entry is [string, ProposeModelId] => PROPOSE_ALLOWED_MODELS.some((model) => model.id === entry[1])),
);

/** 廃止したモデルの表示名 (編集履歴などで旧 id をそのまま出さないため)。選択肢には出さない。 */
export const PROPOSE_RETIRED_MODEL_NAMES: Record<string, { family: string; name: string }> = {
  'gpt-5.6-sol': { family: 'GPT', name: '5.6 Sol' },
  'gpt-5.6-terra': { family: 'GPT', name: '5.6 Terra' },
  'gpt-5.6-luna': { family: 'GPT', name: '5.6 Luna' },
  'claude-fable-5': { family: 'Claude', name: 'Fable 5' },
  'claude-opus-5': { family: 'Claude', name: 'Opus 5' },
  'claude-sonnet-5': { family: 'Claude', name: 'Sonnet 5' },
  'gpt-6-sol': { family: 'GPT', name: '6 Sol' },
};

/** 保存値・復元値のモデル id を現行の選択肢に読み替える。選択肢に無いものは null。 */
export function normalizeProposeModel(id: unknown): ProposeModelId | null {
  if (typeof id !== 'string') return null;
  const candidate = PROPOSE_LEGACY_MODEL_MAP[id] ?? id;
  return PROPOSE_ALLOWED_MODELS.find((model) => model.id === candidate)?.id ?? null;
}

/** AIおまかせ・編集指示は同じ全モデルを選択可能。 */
export const AUTOPILOT_ALLOWED_MODEL_IDS: readonly ProposeModelId[] = PROPOSE_ALLOWED_MODELS.map((model) => model.id);

export function isAutopilotModel(id: ProposeModelId): boolean {
  return AUTOPILOT_ALLOWED_MODEL_IDS.includes(id);
}

/** AI 提案が破棄したアイテムの理由 (存在しない clipId・尺不整合など)。 */
export interface ProposeDroppedItem {
  clipId: string;
  reason: string;
}

/** POST /video-edit-projects/{id}/propose のジョブ result_data。 */
/**
 * AI ジョブの実行中にサーバーが project 行へ書いた導出値と、その結果の**新しい revision**
 * (§4.3-T の P1-3)。
 *
 * BGM のビート検出 (`bgmAnalysis`) は AI 実行の直前にサーバーが補うことがあり、その保存で
 * revision が 1 つ上がる。フロントは再読込しないので、これを受け取って
 * **ユーザーの未保存編集を保ったまま** `bgmAnalysis` をマージし `revision` を更新する
 * (更新しないと、次の保存が 409 (競合) になる)。
 */
export interface VideoEditProjectPatch {
  bgmAnalysis?: Record<string, BgmAnalysis> | null;
  /** サーバーが解析結果を保存した後の revision。 */
  revision?: number | null;
  /**
   * 解析結果を保存する直前の revision (= revision - 1)。フロントはこれが自分の持つ revision と
   * 一致するときだけ revision へ進める。一致しない = 別画面がその間に保存しているので、
   * 進めると楽観ロックをすり抜けて別画面の変更を上書きしてしまう (競合として扱う)。
   * 2026-09-14 より前のジョブ記録 / pending_proposal には無いので省略可 (その場合は従来どおり進める)。
   */
  baseRevision?: number | null;
}

export interface ProposeResult {
  items: TimelineItem[];
  narrations: NarrationCue[];
  /**
   * BGM トラックの変更提案 (docs/features/video-editor-bgm-track-plan.md §5.3 (b))。**三値**:
   * - キー自体が無い / null = 変更なし (元の bgmTrack を保持)
   * - [] = 全削除 (BGM なし)
   * - 配列 = 差し替え
   * 移行期の AI 出力・古い pending_proposal は旧キー `bgm` (単一オブジェクト) のことがあるので、
   * 適用側 (VideoEditorPage.applyProposal) が `legacyBgmToCue` で 1 キューへ変換する
   * (新旧両方あれば新形式を優先)。
   */
  bgmTrack?: AudioCue[] | null;
  /** @deprecated 旧形式 (単一 BGM)。読み取り側の互換のためだけに残す (書かない)。 */
  bgm?: LegacyBgm | null;
  /**
   * SE (効果音) トラックの変更提案 (docs/features/video-editor-capability-gaps-plan.md §4.3-D)。
   * BGM と同じ**三値**: キー自体が無い / null = 変更なし・[] = 全解除・配列 = 差し替え。
   * 2026-09-12 追加。それ以前に保存された pending_proposal には無いので省略可。
   */
  seTrack?: AudioCue[] | null;
  /**
   * 候補 SE (`seCandidates`) の更新 (§4.3-D)。AI が運営提供カタログの効果音を使ったときだけ付き、
   * ジョブ完了時にサーバーが組織の素材へ複製して materialId を埋めた**一覧まるごと**が入る。
   * 省略 / null = 変更なし (プロジェクトの候補をそのまま保つ)。
   */
  seCandidates?: SeCandidate[] | null;
  reasoning: string;
  dropped: ProposeDroppedItem[];
  usage: { input_tokens: number; output_tokens: number } | null;
  model: string;
  /** AI が決めたこの編集の特徴を端的に表すプロジェクト名 (12文字以内)。決定できなかった場合 null。 */
  projectName: string | null;
  /**
   * トラック全体の「動画に含めない」の変更提案 (含まれるトラックだけ上書き)。提案が無ければ null。
   * 2026-09-10 追加。それ以前に保存された pending_proposal には無いので省略可。
   */
  trackMutes?: Partial<TrackMutes> | null;
  /**
   * ジョブ中にサーバーが project 行へ書いた導出値と新しい revision (§4.3-T の P1-3)。
   * 適用時に `bgmAnalysis` をマージして `revision` を合わせ直す。省略 = 何も書いていない。
   */
  projectPatch?: VideoEditProjectPatch | null;
  /**
   * カラーグレーディングの変更提案。3 値の意味を区別する (enabled は付いてこないので
   * FE 側で付与する):
   * - キー自体が無い (省略) = 提案なし。現状維持
   * - null = カラーグレーディング解除
   * - { preset, intensity } = そのカラーグレーディングを適用
   * 2026-09-10 追加。それ以前に保存された pending_proposal には無いので省略可。
   */
  look?: { preset: LookPreset; intensity: number } | null;
  /**
   * ナレーション中の BGM 自動ダッキングの変更提案 (§4.3-E)。look と同じ 3 値:
   * - キー自体が無い (省略) = 提案なし。現状維持
   * - null = 無効化 (enabled: false に戻す)
   * - { enabled?, targetDb?, attackSec?, releaseSec?, holdSec? } = その設定を適用
   *   (欠けたキーは既定値・範囲外はクランプ)
   * 2026-09-12 追加。それ以前に保存された pending_proposal には無いので省略可。
   */
  ducking?: Partial<Ducking> | null;
  /**
   * 書き出し時のラウドネス正規化の変更提案 (§4.3-M)。ducking と同じ 3 値:
   * - キー自体が無い (省略) = 提案なし。現状維持
   * - null = 無効化 (enabled: false に戻す)
   * - { enabled?, targetLufs?, truePeakDb?, lra? } = その設定を適用
   * 2026-09-13 追加。それ以前に保存された pending_proposal には無いので省略可。
   */
  loudness?: Partial<Loudness> | null;
  /**
   * 動画全体に効くオーバーレイの変更提案 (§4.1-F)。3 値 (look / ducking と違い null は現状維持):
   * - キー自体が無い (省略) / null = 提案なし。現状維持
   * - `[]` = 全解除
   * - 配列 = その配列で置き換え
   * 2026-09-12 追加。それ以前に保存された pending_proposal には無いので省略可。
   */
  overlays?: Overlay[] | null;
}

/**
 * 未適用の AI 編集提案 (video_edit_projects.pending_proposal)。
 * 形状の正本はバックエンド (executors.py build_pending_proposal_record)。result は ProposeResult から
 * usage (トークン数。usage_logs に別途記録済み) を除いたもの。
 * おまかせ (autopilot) の最良の周を保存した場合、result.applied に引き継ぎ処理済みの適用結果が入る
 * (復元時は引き継ぎ処理を掛けずそのまま載せる。「指示どおり」の提案には無い)。
 */
export interface PendingProposalRecord {
  job_id: string;
  created_at: string;
  instruction: string;
  target_duration_sec: number | null;
  model: string;
  result: Omit<ProposeResult, 'usage'> & { applied?: AutopilotApplied };
  /**
   * おまかせ (autopilot) の最良の周を保存した場合に付く要約 (executors.py build_pending_autopilot_summary
   * が正本)。復元時に「AI が立てた方針」「周ごとの評価」「シナリオ側の提案」をパネルに出すために使う
   * (「指示どおり」の提案には無い)。
   */
  autopilot?: PendingAutopilotSummary | null;
}

/** 周ごとのレコードのうち表示に使う部分 (proposal / applied / issues を除いたもの)。 */
export type AutopilotIterationSummary = Pick<
  AutopilotIterationRecord,
  'iteration' | 'evaluation' | 'score' | 'passed' | 'high_issue_count' | 'summary'
>;

/** pending_proposal に添える「おまかせ」の要約。 */
export interface PendingAutopilotSummary {
  brief: AutopilotBrief | null;
  scenario_suggestions: ScenarioSuggestion[];
  best_iteration: number | null;
  stop_reason: AutopilotStopReason;
  interrupted: boolean;
  iterations: AutopilotIterationSummary[];
}

/**
 * 未適用の AI 編集提案を消す (適用した / 破棄したとき)。jobId を渡すと、保存されている提案が
 * その job のものである場合だけ消す (適用の間により新しい提案が完了していたらそちらは残す)。
 * 404 (既に無い / 別の job に置き換わっている) は目的を達しているので成功として扱う。
 */
export async function clearPendingProposal(projectId: string, jobId?: string): Promise<void> {
  try {
    await apiClient.delete(`/video-edit-projects/${projectId}/pending-proposal`, {
      params: jobId ? { job_id: jobId } : undefined,
    });
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) return;
    throw error;
  }
}

/**
 * AI 編集提案ジョブを作成する。202 で job_id を返す。
 * 有償 (2026-09-04〜): 受付時に 1 回分のポイント (credit_rate_config の video_edit /
 * video-edit-propose) をデポジット控除し、ジョブ失敗・タイムアウト時はサーバー側で返還される。
 * 残高不足は 402 (response.data.error_code === 'INSUFFICIENT_CREDITS'、error に日本語文言)。
 * 完了待ちは asyncJobApi.ts の waitForJobCompletion / getJobStatus を使う。
 */
export async function createProposeJob(
  projectId: string,
  params: { instruction: string; targetDurationSec?: number; model?: ProposeModelId },
): Promise<{ job_id: string }> {
  const { data } = await apiClient.post<{ job_id: string }>(`/video-edit-projects/${projectId}/propose`, {
    instruction: params.instruction,
    ...(params.targetDurationSec !== undefined ? { target_duration_sec: params.targetDurationSec } : {}),
    ...(params.model !== undefined ? { model: params.model } : {}),
  });
  return data;
}

// ── シナリオ側の提案 (旧 AI 編集アドバイザーの成果物。2026-09-11 おまかねへ統合) ────────
// 旧 advise 機能 (POST /<id>/advise) は廃止し、おまかせ (autopilot) のディレクター工程に統合した
// (docs/features/video-editor-autopilot-plan.md §14)。scenarioSuggestions の型と表示ラベルは
// そのまま引き継ぐ (proposal/scenarioSuggestionLabels.ts が表示に使う)。

/**
 * シナリオ側の提案の種類 (backend advise_service.SCENARIO_SUGGESTION_KINDS と同じ allowlist)。
 * 未知の値 (サーバーが将来 kind を追加した場合) にも耐えるよう string へのフォールバックを許容する
 * (frontend/src/features/videoEditor/proposal/scenarioSuggestionLabels.ts が「その他」表示にする)。
 */
export type ScenarioSuggestionKind =
  | 'regenerate_scene'
  | 'generate_bgm'
  | 'regenerate_bgm'
  | 'add_end_card'
  | 'regenerate_narration'
  | 'add_scene'
  | 'other'
  | string;

/**
 * シナリオ / ストーリーボード画面 (動画エディタの外) で行う編集の選択肢 1 件。
 * クリックしても指示欄には反映しない、表示専用の提案。
 */
export interface ScenarioSuggestion {
  kind: ScenarioSuggestionKind;
  title: string;
  /** 対象のシーン/クリップ (人が読める表現。無ければ空文字)。 */
  target: string;
  detail: string;
}

// ── AI 編集の「おまかせ」(autopilot。2026-09 追加・同 11 日 2 モード構成へ改訂) ─────────
// backend/routes/video_edit/projects.py (POST /<id>/autopilot) に対応。ディレクター工程
// (演出意図書 = brief を書く) → 本ループ (AI 編集 (propose) を「提案 → 評価 → 修正」で最大 5 周
// 回す) の 2 段構成。周ごとの結果は GET /api/jobs/<job_id> の processing 中のペイロード
// (job.autopilot。asyncJobApi.ts の AsyncJob.autopilot) で公開され、hooks/useAutopilotRun.ts が
// 周ごとにタイムラインへ即時反映する (バックエンドが引き継ぎ処理まで済ませた `applied` をそのまま
// 載せ、フロント側では引き継ぎ処理を再実行しない。docs/features/video-editor-autopilot-plan.md §6.4)。

/** 1 周の評価で見つかった問題点。source はコード検査 (lint) か AI 批評 (critique) か。 */
export interface AutopilotIssue {
  severity: 'high' | 'medium' | 'low';
  area: string;
  description: string;
  fix: string;
  source: 'lint' | 'critique';
}

/** 評価状態。skipped/failed は未評価 (合格にはならない)。 */
export type AutopilotEvaluation = 'scored' | 'skipped' | 'failed';

/**
 * 引き継ぎ処理 (テロップデザイン維持・話者復元・音量/ミュート/シーン名の継承・trackMutes/look の
 * 3 値マージ) 適用後の、実際に画面へ載る最終的なタイムライン一式。批評・次周の入力・画面反映は
 * すべてこれを使う。ProposeResult と違い、いずれのキーも「省略 = 現状維持」ではなく確定値
 * (trackMutes/look もこの周で実際に載る最終値)。
 */
export interface AutopilotApplied {
  timeline: TimelineItem[];
  narrationTrack: NarrationCue[];
  /** BGM トラック (確定値。BGM なしは [])。古いジョブの記録には旧キー `bgm` が入っていることがある */
  bgmTrack: AudioCue[];
  /** @deprecated 旧形式 (単一 BGM)。古い pending_proposal / job 記録の読み取り互換のためだけに残す。 */
  bgm?: LegacyBgm | null;
  /** SE (効果音) トラック (確定値。効果音なしは [])。2026-09-12 より前の記録には無いので省略可。 */
  seTrack?: AudioCue[];
  /** 候補 SE の更新 (§4.3-D。カタログの効果音を複製したときだけ付く)。 */
  seCandidates?: SeCandidate[];
  trackMutes: TrackMutes;
  look: Look | null;
  /** ダッキング設定 (確定値。§4.3-E)。2026-09-12 より前の記録には無いので省略可。 */
  ducking?: Ducking | null;
  /** ラウドネス正規化 (確定値。§4.3-M)。2026-09-13 より前の記録には無いので省略可。 */
  loudness?: Loudness | null;
  /** 動画全体のオーバーレイ (確定値。§4.1-F)。2026-09-12 より前の記録には無いので省略可。 */
  overlays?: Overlay[] | null;
  name: string | null;
}

/** 1 周分の評価結果 (提案・適用結果を含む)。 */
export interface AutopilotIterationRecord {
  iteration: number;
  evaluation: AutopilotEvaluation;
  score: number | null;
  passed: boolean;
  high_issue_count: number;
  summary: string;
  issues: AutopilotIssue[];
  /** AI が返した生の提案 (監査用)。 */
  proposal: ProposeResult;
  /** 引き継ぎ処理後の適用結果。画面にはこれをそのまま載せる。 */
  applied: AutopilotApplied;
}

/**
 * 編集の実況 (§19)。おまかせ実行中、AI の操作を 1 件ずつプレビューへ反映して見せるための
 * 進捗キー (job.autopilot.live)。processing 中のみ付き、周が確定すると次の周の live に
 * 置き換わる。**表示専用**で、確定は従来どおり周のレコード (AutopilotIterationRecord.applied)
 * が行う (実況の途中状態が最終と食い違っても、周のレコード到着で必ず上書きされる)。
 * docs/features/video-editor-autopilot-plan.md §19.2 の契約に対応。
 */
export type AutopilotLiveMode = 'initial' | 'ops';

/** live 全体の段階。streaming=AI 応答の読み取り中 / applying=適用・検証中 / done=この周の実況は終わり。 */
export type AutopilotLiveStatus = 'streaming' | 'applying' | 'done';

/** 操作 1 件の状態。streaming=まだ検証前 (絵は変えない) / applied=適用済み (patch あり) / rejected=見送り。 */
export type AutopilotLiveOpStatus = 'streaming' | 'applied' | 'rejected';

/**
 * 実況が表示に使う差分。applied のエントリにだけ付く。
 * - item: 同じ id のアイテムを差し替える (テロップ系・範囲・速度・音声・トランジション・名前・除外/採用)
 * - timeline: 配列全体を差し替える (move_item・add_item・apply_caption_design・テロップ同期)
 * - narrationTrack: 配列全体を差し替える (ナレーション系・リップル)
 * - bgm: BGM トラック全体 (`bgmTrack`) を差し替える (null は送らない。変更が無ければパッチ自体が出ない)
 * - se: SE (効果音) トラック全体 (`seTrack`) を差し替える (bgm と同じ扱い)
 * - overlays: 動画全体のオーバーレイ配列を差し替える (§4.1-F)
 * - trackMutes / look / name: それぞれの値を差し替える
 */
export type AutopilotLivePatch =
  | { kind: 'item'; item: TimelineItem }
  | { kind: 'timeline'; timeline: TimelineItem[] }
  | { kind: 'narrationTrack'; narrationTrack: NarrationCue[] }
  // bgmTrack は三値 (§5.3 (b)): 省略 / null = 変更なし・[] = 全削除・配列 = 差し替え。
  // bgm は移行期の旧形式 (単一オブジェクト) で、受け取ったら 1 キューへ変換して差し替える。
  | { kind: 'bgm'; bgmTrack?: AudioCue[] | null; bgm?: LegacyBgm | null }
  // seTrack も三値 (§4.3-D)。省略 / null = 変更なし・[] = 全解除・配列 = 差し替え。
  | { kind: 'se'; seTrack?: AudioCue[] | null }
  | { kind: 'trackMutes'; trackMutes: TrackMutes }
  | { kind: 'look'; look: Look | null }
  // ナレーション中の BGM 自動ダッキング (§4.3-E)。プロジェクト単位の設定 1 キーの差し替え。
  | { kind: 'ducking'; ducking: Ducking | null }
  // 書き出し時のラウドネス正規化 (§4.3-M)。プロジェクト単位の設定 1 キーの差し替え。
  | { kind: 'loudness'; loudness: Loudness | null }
  // 動画全体のオーバーレイ (§4.1-F)。プロジェクト直下の配列を丸ごと差し替える
  // (シーンのオーバーレイは kind: 'item' のアイテム差し替えで届く)。
  | { kind: 'overlays'; overlays: Overlay[] }
  | { kind: 'name'; name: string }
  // ビートへの吸着 (§4.3-T)。カット点 (timeline) と効果音 (seTrack) の両方が動くので、
  // 2 つをまとめて差し替える専用のパッチ。
  | { kind: 'beatSnap'; timeline: TimelineItem[]; seTrack: AudioCue[] }
  /** 最良の周へ戻す (退行した周を捨てる)。6 キーをまとめて差し替える。 */
  | { kind: 'applied'; applied: AutopilotApplied };

/** 操作が指す対象 (適用前の番号。§18.3)。欠落・null は「対象なし」。 */
export interface AutopilotLiveOpTarget {
  scene?: number | null;
  caption?: number | null;
  narration?: number | null;
  /** SE 番号 (1 始まり。§4.3-D の add_se / remove_se / move_se / set_se_volume が指す番号)。 */
  se?: number | null;
  /**
   * BGM キューの番号 (1 始まり。docs/features/video-editor-bgm-track-plan.md §9.2 の
   * split_bgm / set_bgm_cue / remove_bgm_cue)。シーンに紐づかないので強調 (発光) の対象には
   * ならず、プレビューは seek_sec (音の変わり目の秒) だけで寄せる。
   */
  cue?: number | null;
}

/** 実況 1 行 = 操作 1 件。 */
export interface AutopilotLiveOpEntry {
  /** 操作列内の位置 (0 始まり)。実況はこの順に消費する。 */
  index: number;
  /** 操作の種類 (set_caption・move_item・split_bgm・ripple_narrations・ripple_bgm・sync_captions 等)。 */
  op: string;
  /** 表示用の日本語ラベル (バックエンドが生成する)。 */
  label: string;
  target?: AutopilotLiveOpTarget | null;
  status: AutopilotLiveOpStatus;
  /** rejected のときの却下理由。 */
  reason?: string | null;
  /** applied のとき、プレビューを寄せる絶対秒 (無ければ null)。 */
  seek_sec?: number | null;
  /** applied のときの表示用差分。 */
  patch?: AutopilotLivePatch | null;
}

/** 実況の本体 (AutopilotProgress.live)。 */
export interface AutopilotLive {
  /** 実況中の周 (1 始まり)。 */
  iteration: number;
  mode: AutopilotLiveMode;
  status: AutopilotLiveStatus;
  /** mode=initial: 生成中に読み取れた items の件数 (「AI が n シーン目を書いています」)。 */
  streamed_item_count?: number | null;
  /** mode=ops: 操作の実況 (index 順)。mode=initial では空配列。 */
  ops: AutopilotLiveOpEntry[];
}

export type AutopilotPhase = 'directing' | 'proposing' | 'evaluating' | 'finishing';

/** ディレクター工程 (§6.0) の成果。 */
export interface AutopilotBrief {
  editBrief: string;
  overview: string;
}

/** GET /jobs/{id} が processing 中に返す追加キー (job.autopilot)。 */
export interface AutopilotProgress {
  max_iterations: number;
  /** 着手中の周 (1 始まり。ディレクター工程中は 0)。 */
  iteration: number;
  phase: AutopilotPhase;
  interrupt_requested: boolean;
  /** 完成済みの最新の周 (0 = まだ無い)。 */
  latest_iteration: number;
  /** ディレクター工程の成果。完了前は null。 */
  brief: AutopilotBrief | null;
  scenario_suggestions: ScenarioSuggestion[];
  /** 完成した周をすべて保持する (最新だけではない。取りこぼし復元のため)。 */
  iterations: AutopilotIterationRecord[];
  /**
   * 編集の実況 (§19)。実況の生成に失敗しても周は止めないため、欠落 / null を取りうる
   * (その場合は従来どおり phase 表示だけになる)。
   */
  live?: AutopilotLive | null;
}

export type AutopilotStopReason =
  | 'time_limit'
  | 'passed'
  | 'no_improvement'
  | 'max_iterations'
  | 'interrupted'
  | 'evaluation_failed'
  | 'error_after_best'
  /** 巻き戻した周の指摘 (または適用結果) が最良の周とまったく同じで、残りの周を打ち切った。 */
  | 'stuck';

export type AutopilotOutcomeKind = 'best' | 'no_result';

/** 完了時の result_data。 */
export interface AutopilotResult {
  _autopilot_settlement?: AutopilotSettlement;
  outcome: AutopilotOutcomeKind;
  /** no_result のときは null。 */
  best_iteration: number | null;
  stop_reason: AutopilotStopReason;
  interrupted: boolean;
  iterations: AutopilotIterationRecord[];
  /** 最良の周の提案 (生)。no_result のときは null。 */
  result: ProposeResult | null;
  /** 最良の周の適用結果。no_result のときは null。 */
  applied: AutopilotApplied | null;
  /** ディレクター工程が終わる前の中断では null。 */
  brief: AutopilotBrief | null;
  scenario_suggestions: ScenarioSuggestion[];
  model: string;
  /**
   * stop_reason='error_after_best' のときの失敗内容 (n 周目の提案生成がプロバイダー由来のエラーで
   * 失敗し、直前までの最良で終了した)。それ以外は null / 欠落。
   */
  failure?: AutopilotFailure | null;
  /**
   * ジョブ中にサーバーが project 行へ書いた導出値と新しい revision (§4.3-T の P1-3)。
   * 完了時に `bgmAnalysis` をマージして `revision` を合わせ直す。
   */
  project_patch?: VideoEditProjectPatch | null;
}

export interface AutopilotFailure {
  iteration: number;
  stage: 'propose_edit' | 'revise_with_ops' | string;
  error_type: string;
  message: string;
}

/**
 * ProposePanel が「おまかせ」ジョブの作成に成功した直後、親 (VideoEditorPage) の
 * onAutopilotStart に渡す情報 (hooks/useAutopilotRun.ts がそのままポーリングに使う)。
 */
export interface AutopilotRunMeta {
  jobId: string;
  projectId: string;
  /** 演出プランを作ったときの要望 (空文字可)。編集履歴の表示には使わない (brief.editBrief を使う)。 */
  request: string;
  model: string;
  keepCaptionDesign: boolean;
  maxIterations: number;
}

/**
 * おまかせ (autopilot) ジョブを作成する。202 で job_id を返す。propose と同じデポジット課金
 * (credit_rate_config の video_edit / video-edit-autopilot-iteration × 最大ダメ出し回数)。残高不足は402、
 * レート未設定は503。完了した回数で精算し、未完了分を返還する。
 * 二段階化 (2026-09-17): 確定した演出プランのジョブ (directorJobId) と表示単価を送る。要望・目標尺・
 * モデルはサーバーが演出プランのジョブから取る (再送しない)。プラン作成後に編集内容が変わっていても
 * 拒否されない (サーバーが手動編集の要約を編集 AI に渡す。2026-09-18)。instruction は送らない。
 */
export async function createAutopilotJob(
  projectId: string,
  params: {
    maxIterations?: number;
    keepCaptionDesign?: boolean;
    directorJobId: string;
    quotedUnitCredits: number;
  },
): Promise<{ job_id: string }> {
  const { data } = await apiClient.post<{ job_id: string }>(`/video-edit-projects/${projectId}/autopilot`, {
    billing_mode: 'per_iteration',
    director_job_id: params.directorJobId,
    quoted_unit_credits: params.quotedUnitCredits,
    ...(params.maxIterations !== undefined ? { max_iterations: params.maxIterations } : {}),
    ...(params.keepCaptionDesign !== undefined ? { keep_caption_design: params.keepCaptionDesign } : {}),
  });
  return data;
}

export interface AutopilotSettlement {
  status: 'pending' | 'done' | 'failed';
  requested_iterations: number;
  completed_iterations: number;
  unit_credits: number;
  reserved_credits: number;
  charged_credits: number;
  refund_credits: number;
}

// ── AIディレクターの演出プラン (二段階化 2026-09-17。docs/features/video-editor-director-brief-stage.md) ──

/** 演出プランのカードで確認する質問 1 件。 */
export interface DirectorQuestion {
  id: string;
  question: string;
  options: string[];
}

/** 回答・要望のやりとり 1 件 (サーバー保存値)。 */
export type DirectorTranscriptEntry =
  | { kind: 'answer'; id?: string; question: string; answer: string }
  | { kind: 'skipped'; id?: string; question: string }
  | { kind: 'message'; text: string; reply: string };

/** 演出プランのジョブの結果 (GET /jobs/<id>.result_data / GET /<id>/director/latest.director)。 */
export interface DirectorResult {
  overview: string;
  editBrief: string;
  scenarioSuggestions: ScenarioSuggestion[];
  questions: DirectorQuestion[];
  transcript: DirectorTranscriptEntry[];
  /** 「今回の要望」への返答 (修正ジョブで要望を送ったときだけ)。 */
  reply?: string;
  context: { request: string; target_duration_sec: number | null; model: string; project_revision: number };
  root_job_id: string;
  revision_no: number;
}

/** 演出プランを作る (有償 50pt・デポジット)。単価不一致は 409、レート未設定は 503、残高不足は 402。 */
export async function createDirectorJob(projectId: string, params: {
  request: string;
  model: ProposeModelId;
  targetDurationSec?: number;
  quotedCredits: number;
}, signal?: AbortSignal): Promise<{ job_id: string }> {
  const { data } = await apiClient.post<{ job_id: string }>(`/video-edit-projects/${projectId}/director`, {
    request: params.request, model: params.model, target_duration_sec: params.targetDurationSec,
    quoted_credits: params.quotedCredits,
  }, { signal });
  return data;
}

/**
 * 演出プランを回答・要望で直す (無料・何度でも)。answers の answer=null は「無回答」(AI が決める)。
 * プラン作成後に編集内容が変わっていても受け付け、現在の保存済みの編集内容でプランを書き直す (2026-09-18)。
 */
export async function reviseDirectorJob(projectId: string, jobId: string, params: {
  answers?: { id: string; answer: string | null }[];
  message?: string;
}, signal?: AbortSignal): Promise<{ job_id: string }> {
  const { data } = await apiClient.post<{ job_id: string }>(
    `/video-edit-projects/${projectId}/director/${jobId}/revise`,
    { answers: params.answers, message: params.message },
    { signal },
  );
  return data;
}

/** 再訪時の復元で返る最新の演出プラン。started=編集を開始済み (表示のみ)。 */
export type LatestDirector = DirectorResult & { job_id: string; started?: boolean };

/**
 * 実行中 (pending / processing) の演出プランのジョブ。parent_job_id があれば修正、無ければ作成。
 * フロントはこのジョブの完了待ちに戻る (リロード後に作り直して再課金しないため。2026-09-17 レビュー指摘 4)。
 */
export interface PendingDirectorJob {
  job_id: string;
  parent_job_id: string | null;
}

export interface LatestDirectorResponse {
  /** 最新の完了した演出プラン (無ければ null)。 */
  director: LatestDirector | null;
  /** 実行中の作成・修正ジョブ (無ければ null)。 */
  pending: PendingDirectorJob | null;
}

/** 再訪時の復元: 最新の演出プランと、実行中の作成・修正ジョブ (どちらも無ければ null)。 */
export async function fetchLatestDirector(projectId: string, signal?: AbortSignal): Promise<LatestDirectorResponse> {
  const { data } = await apiClient.get<Partial<LatestDirectorResponse>>(
    `/video-edit-projects/${projectId}/director/latest`, { signal },
  );
  return { director: data.director ?? null, pending: data.pending ?? null };
}

/** ジョブが中断できない状態 (pending/processing 以外) だったときのエラー (409)。 */
export class JobNotInterruptibleError extends Error {
  constructor(message = 'このジョブは中断できません') {
    super(message);
    this.name = 'JobNotInterruptibleError';
  }
}

/** POST /jobs/{id}/interrupt の応答。 */
export interface InterruptJobResponse {
  success: true;
  /**
   * 'cancelled': ジョブがまだ pending (実行体が動いていない) だったため、既存のキャンセルに
   * 委譲して全額返金した (ジョブ行は削除され、以後 GET /jobs/<id> は 404 になる)。
   * 'interrupt': 実行中のジョブに中断フラグを立てた (返金なし。ジョブは最良の結果を持って終了する)。
   */
  mode: 'cancelled' | 'interrupt';
}

/**
 * 実行中のジョブに中断をリクエストする (POST /jobs/{id}/interrupt)。応答の mode で分岐する
 * (呼び出し側 (hooks/useAutopilotRun.ts) が 'cancelled' ならポーリングを打ち切ってロックを解除し、
 * 'interrupt' ならジョブの終端を待つ)。ジョブが pending/processing 以外 (既に完了・失敗している等)
 * のときは 409 → JobNotInterruptibleError を throw する。
 */
export async function interruptJob(jobId: string): Promise<InterruptJobResponse> {
  try {
    const { data } = await apiClient.post<InterruptJobResponse>(`/jobs/${jobId}/interrupt`);
    return data;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 409) {
      const message = (error.response.data as { error?: string } | undefined)?.error;
      throw new JobNotInterruptibleError(message);
    }
    throw error;
  }
}

// ── 書き出し (レンダリング) ────────────────────────────────────────
// backend/routes/video_edit_projects.py (POST /<id>/render) + backend/routes/async_jobs/video_edit.py
// に対応。ジョブ完了待ちは asyncJobApi.ts の getJobStatus を自前ポーリングして進捗 (progress) を
// 表示する (waitForJobCompletion は進捗を露出しないため ExportPanel では使わない)。

/** POST /video-edit-projects/{id}/render のジョブ result_data。 */
export interface RenderResult {
  material_id: string;
  output: {
    durationInFrames: number;
    width: number;
    height: number;
    fps: number;
    /**
     * ラウドネス正規化の結果 (§4.3-M)。レンダラー (video-editor の src/lib/loudness.ts) が
     * 書き出し後の後処理として返す注記で、**設定が無効なとき・失敗したときも付く**
     * (applied: false + reason)。レンダラーが古い場合は欠落するので省略可。
     */
    loudness?: {
      /** プロジェクトで正規化が有効だったか (掛けようとしたか)。UI はこれで表示要否を決める。 */
      requested?: boolean;
      applied: boolean;
      reason?: string;
      targetLufs?: number;
      truePeakDb?: number;
      measuredInputI?: number;
      measuredInputTp?: number;
    };
  };
}

/**
 * レンダリング (書き出し) ジョブを作成する。202 で job_id を返す。
 */
export async function createRenderJob(projectId: string): Promise<{ job_id: string }> {
  const { data } = await apiClient.post<{ job_id: string }>(`/video-edit-projects/${projectId}/render`);
  return data;
}

// ── 解析 (無音・シーンチェンジ・代表フレーム抽出) ──────────────────
// backend/routes/video_edit_projects.py (POST /<id>/analyze) に対応。サーバ側で project jsonb の
// sourceClips[].analysis を書き換えるため、完了後は呼び出し側でプロジェクトを再読込し
// revision のずれ (次回保存時の 409) を防ぐこと。

export interface CreateAnalyzeJobResponse {
  job_id: string;
  /**
   * 表示用ヒント。true なら同一シナリオの他プロジェクトで同じ素材が解析済みで、ジョブは
   * 解析サービスを呼ばず既存結果のコピーだけで完了する (バックエンド will_reuse_all_analyses)。
   * ローディング画面の文言を「解析中」ではなく「解析済み情報を読み込み中」に切り替えるために使う。
   */
  reusing_existing?: boolean;
}

/**
 * 解析ジョブを作成する。202 で job_id (+ 再利用ヒント) を返す。
 * 完了待ちは asyncJobApi.ts の getJobStatus を自前ポーリングする (useAnalyzeJob)。
 */
export async function createAnalyzeJob(
  projectId: string,
  options?: {
    /** true なら解析済みクリップも含めて全件を解析し直す (シーン分割のやり直し。バックエンド force)。 */
    force?: boolean;
  },
): Promise<CreateAnalyzeJobResponse> {
  const { data } = await apiClient.post<CreateAnalyzeJobResponse>(
    `/video-edit-projects/${projectId}/analyze`,
    options?.force ? { force: true } : {},
  );
  return data;
}

// ── テロップを発話タイミングへ合わせる (手動適用。P4) ────────────────
// backend/routes/video_edit_projects.py (POST /<id>/align-captions) に対応。
// backend/services/video_edit/caption_alignment.py (音声認識結果 sourceClips[].analysis.transcriptWords
// との文字単位アライメント) を使い、リクエストのタイムライン (未保存の編集内容を含んでよい) の
// captions を発話タイミングへスナップする。DB への保存は行わない (結果を適用した project は
// 呼び出し側が通常の PUT で保存する)。

export interface AlignCaptionsRequestCaption {
  id: string;
  text: string;
  /**
   * 単語ハイライト (§4.2-B2) が有効なテロップにだけ、その設定だけを載せた部分 design を送る。
   * サーバーはこれが有効なテロップにだけ対応づけ (wordTimings) を作る
   * (無効なテロップに導出値を付けて保存形を汚さないため)。
   */
  design?: { wordHighlight?: CaptionWordHighlight };
}

export interface AlignCaptionsRequestItem {
  id: string;
  clipId: string;
  inSec: number;
  outSec: number;
  speed?: number;
  /**
   * フリーズフレーム / 速度ランプ (§4.1-L)。表示秒 ⇔ 素材秒の変換に効くので、speed と一緒に送る
   * (送らないとサーバー側の同期がフリーズ / ランプの無いシーンとして計算し、
   * おまかせ経路の同期結果とずれる)。
   */
  freeze?: Freeze;
  speedRamp?: SpeedRamp;
  /** 画面上の開始秒 (コンポジション全体の絶対秒。computeTimelineLayout 由来)。ナレーション cue
   *  (絶対秒) と突き合わせるために送る。除外中など配置されていないアイテムでは省略。 */
  startSec?: number;
  captions: AlignCaptionsRequestCaption[];
}

/** ナレーション cue (narrationTrack)。clipId の clip.analysis.narration (ナレーション音源の文字起こし) を、
 *  startSec (絶対秒) から鳴るものとして同期に使う。muted の cue は使わない。 */
export interface AlignCaptionsNarrationCue {
  clipId: string;
  startSec: number;
  muted?: boolean;
}

/** アライメントに採用された caption のみ (据え置きは含まれない)。 */
export interface AlignedCaptionResult {
  id: string;
  startSec: number;
  endSec: number;
  /** 0..1。正規化後の行の文字数に対する一致文字数の割合。 */
  matchRatio: number;
  /**
   * 単語ハイライト (§4.2-B2) の対応づけ。`design.wordHighlight` が有効なテロップで、
   * 単語単位まで対応づけできたときだけ付く (作れなかったテロップでは `null`。
   * フロントは null を受け取ったら既存の対応づけを**消す** = ハイライト無しに戻す)。
   */
  wordTimings?: CaptionWordTimings | null;
}

export interface AlignCaptionsResponseItem {
  id: string;
  captions: AlignedCaptionResult[];
}

export interface AlignCaptionsStats {
  /** スナップされた caption 数。 */
  aligned: number;
  /** 単語ハイライトの対応づけを作れた caption 数 (§4.2-B2。旧サーバーでは省略)。 */
  wordTimingsBuilt?: number;
  /** 単語ハイライトが有効なのに対応づけを作れなかった caption 数 (同上)。 */
  wordTimingsSkipped?: number;
  /** 照合できず据え置きになった caption 数。 */
  unaligned: number;
  /** aligned のうち、ナレーション音源の文字起こしで照合した caption 数 (旧サーバーでは省略)。 */
  narrationAligned?: number;
  /** timeline が参照する clip のうち、音声認識結果 (映像音声・ナレーションとも) が無かった clip の数 (重複なし)。 */
  clipsWithoutTranscript: number;
}

export interface AlignCaptionsResponse {
  items: AlignCaptionsResponseItem[];
  stats: AlignCaptionsStats;
}

/**
 * テロップを発話タイミングへスナップする。project は更新せず、計算結果のみを返す。
 */
export async function alignCaptionsToSpeech(
  projectId: string,
  timeline: AlignCaptionsRequestItem[],
  narrationTrack: AlignCaptionsNarrationCue[] = [],
): Promise<AlignCaptionsResponse> {
  const { data } = await apiClient.post<AlignCaptionsResponse>(
    `/video-edit-projects/${projectId}/align-captions`,
    { timeline, narrationTrack },
  );
  return data;
}

// ---- ビートへの吸着 (§4.3-T) ----------------------------------------------

/** 吸着した 1 件の明細。 */
export interface SnapToBeatsEntry {
  kind: 'cut' | 'se' | 'effect';
  /** シーン番号 (1 始まり。kind='se' では付かない)。 */
  scene?: number;
  /** 効果音番号 / エフェクト番号 (1 始まり)。 */
  index?: number;
  from_sec: number;
  to_sec: number;
  delta: number;
}

export interface SnapToBeatsResponse {
  timeline: TimelineItem[];
  seTrack: AudioCue[];
  narrationTrack: NarrationCue[];
  bgmTrack: AudioCue[];
  overlays: Overlay[];
  snapped: SnapToBeatsEntry[];
  stats: { snapped: number; beats: number; toleranceSec?: number };
  dropped: Array<{ clipId?: string; reason?: string }>;
}

/**
 * カット点 / 効果音 / 映像エフェクトを BGM の拍へ吸着させる (§4.3-T)。
 *
 * align-captions と同じ「計算だけを返す」API で、**ポイントは消費しない**。
 * project は保存されないので、フロントが結果を履歴に載せて適用したうえで通常の PUT を呼ぶ。
 * 拍が 1 つも分かっていない場合は 409 (エラーメッセージをそのまま出す)。
 */
export async function snapProjectToBeats(
  projectId: string,
  body: {
    timeline: TimelineItem[];
    bgmTrack?: AudioCue[];
    seTrack?: AudioCue[];
    narrationTrack?: NarrationCue[];
    overlays?: Overlay[];
    trackMutes?: TrackMutes;
    targets?: Array<'cuts' | 'se' | 'effects'>;
    toleranceSec?: number;
    scenes?: number[];
  },
): Promise<SnapToBeatsResponse> {
  try {
    const { data } = await apiClient.post<SnapToBeatsResponse>(
      `/video-edit-projects/${projectId}/snap-to-beats`,
      stripResolvedUrls(body),
    );
    return data;
  } catch (error) {
    // 409 = 拍が 1 つも分かっていない (未解析 / 検出できなかった)。サーバーの文言をそのまま出す。
    if (isAxiosError(error) && error.response?.status === 409) {
      const message = (error.response.data as { error?: string } | undefined)?.error;
      throw new Error(message || 'BGM の拍が分かっていません');
    }
    throw error;
  }
}

// ---- ジャンプカット (発話区間だけを残す。jump-cut-plan §7) ----------------------

/** 余白と間の指定 (省略 = backend の既定値。proposal/jumpCut.ts の JUMP_CUT_DEFAULT_PARAMS が同じ値)。 */
export interface JumpCutParams {
  /** 発話の前に残す余白 (秒。0〜1)。 */
  paddingBeforeSec?: number;
  /** 発話の後に残す余白 (秒。0〜1.5)。 */
  paddingAfterSec?: number;
  /** これより短い間は詰めずに残す (秒。0.1〜3)。 */
  minGapSec?: number;
  /** 片の最小の表示尺 (秒。0.2〜2)。 */
  minPieceSec?: number;
  /**
   * ナレーショントラックが鳴っている区間も発話として残す (ナレーションの途中で映像を切らない)。
   * 省略 / false = ナレーションを無視して、映像の埋め込み音声の発話だけで切る。
   */
  includeNarration?: boolean;
  /**
   * 途中の間でシーンを分割してよいか。false = 途中は切らず、前後の空白だけを詰める (1 シーンのまま縮む)。
   * 省略 / true = 分割する。
   */
  allowSplit?: boolean;
}

/** 切った 1 シーンの明細。 */
export interface JumpCutSceneReport {
  /** シーン番号 (1 始まり)。 */
  scene: number | null;
  /** 残した片の数。 */
  pieces: number;
  /** 詰めた秒数 (表示秒)。 */
  removedSec: number;
  /** 残った秒数 (表示秒)。 */
  keptSec: number;
  /** 推定の粗さなどの注記 (無ければ空)。 */
  notes: string[];
  /** 発話区間の取得元。 */
  source: 'words' | 'segments' | 'silences' | null;
}

export interface JumpCutReport {
  /** 実際に切ったシーン (対象外・発話なしのシーンは含まない)。 */
  scenes: JumpCutSceneReport[];
  /** 片の合計。 */
  pieces: number;
  /** 詰めた秒数の合計 (表示秒)。 */
  removedSec: number;
  /** 残った秒数の合計 (表示秒)。 */
  keptSec: number;
  /** 各シーンの注記を「シーンn: 」付きで並べたもの。 */
  notes: string[];
}

export interface JumpCutResponse {
  timeline: TimelineItem[];
  narrationTrack: NarrationCue[];
  bgmTrack: AudioCue[];
  seTrack: AudioCue[];
  overlays: Overlay[];
  report: JumpCutReport;
  dropped: Array<{ clipId?: string; reason?: string }>;
}

/**
 * シナリオ全体を発話のある区間だけに詰める (jump-cut-plan §7)。
 *
 * 発話のある採用中の動画シーンすべてを対象に、サーバーが各シーンを片に分割して間を削除する
 * (静止画・音声なし・未解析・発話なしのシーンは飛ばす)。align-captions / snap-to-beats と同じ
 * 「計算だけを返す」API で、**ポイントは消費しない**。発話区間は保存済み project の解析結果から
 * 取るので、未解析なら先に解析ジョブを通す (VideoEditorPage の ensureAnalyzed)。project は保存
 * されないので、フロントが結果を履歴に載せて適用したうえで通常の PUT を呼ぶ。切れるシーンが
 * 1 つも無ければ 409 (エラーメッセージをそのまま出す)。
 */
export async function jumpCutProject(
  projectId: string,
  body: {
    timeline: TimelineItem[];
    /** 1 シーンだけに掛けるときの item id (省略 = 発話のある採用中シーンすべて)。 */
    scene?: string;
    bgmTrack?: AudioCue[];
    seTrack?: AudioCue[];
    narrationTrack?: NarrationCue[];
    overlays?: Overlay[];
    trackMutes?: TrackMutes;
    params?: JumpCutParams;
  },
): Promise<JumpCutResponse> {
  try {
    const { data } = await apiClient.post<JumpCutResponse>(
      `/video-edit-projects/${projectId}/jump-cut`,
      stripResolvedUrls(body),
    );
    return data;
  } catch (error) {
    // 409 = このシーンには掛けられない (未解析・音声なし・発話なし・間なし等)。サーバーの文言をそのまま出す。
    if (isAxiosError(error) && error.response?.status === 409) {
      const message = (error.response.data as { error?: string } | undefined)?.error;
      throw new Error(message || '間を詰められるシーンがありません');
    }
    throw error;
  }
}

// 署名付き URL 系のキー。バックエンドの _strip_resolved_urls と同じ対象キー名。
// `srcDurationSec` は動画オーバーレイ (§4.1-V) の素材尺で、署名 URL 応答から充填する導出値。
const STRIP_KEYS = new Set(['src', 'proxySrc', 'narrationSrc', 'srcDurationSec']);

/**
 * src / proxySrc / narrationSrc キーをオブジェクトツリー全体 (dict・配列双方) から
 * 再帰削除する。これらは署名付き URL の導出値であり DB には保存しない。
 * POST/PUT 送信前に必ず適用する (バックエンド側の再帰削除と二重防御)。
 */
export function stripResolvedUrls<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((item) => stripResolvedUrls(item)) as unknown as T;
  }
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
      if (STRIP_KEYS.has(key)) continue;
      out[key] = stripResolvedUrls(v);
    }
    return out as T;
  }
  return value;
}
